/**
 * Pendo Public API
 *
 * Pendo library-specific implementations including script injection,
 * initialization, and wrapper methods for the Pendo API.
 */

import { PENDO_CONFIG } from './util/constants';

// =============================================================================
// Pendo Script Loading
// =============================================================================

/**
 * Polyfill for requestIdleCallback.
 * Provides fallback for browsers that don't support requestIdleCallback (Safari, IE).
 * Uses setTimeout as a fallback with a reasonable delay.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/requestIdleCallback
 */
const requestIdleCallbackPolyfill = (function() {
  if (typeof window !== 'undefined' && window.requestIdleCallback) {
    return window.requestIdleCallback.bind(window);
  }

  // Polyfill: Use setTimeout with a delay to simulate idle behavior
  return function(callback, options) {
    const timeout = (options && options.timeout) || PENDO_CONFIG.IDLE_TIMEOUT;
    const start = Date.now();

    return setTimeout(function() {
      callback({
        didTimeout: false,
        timeRemaining: function() {
          return Math.max(0, 50 - (Date.now() - start));
        }
      });
    }, timeout);
  };
})();

/**
 * Injects the Pendo agent script into the page.
 * This is the official Pendo loader snippet.
 * @private
 */
function injectPendoAgent(apiKey, options) {
  (function(pendoKey) {
    (function(p, e, n, d, o) {
      var v, w, x, y, z;
      o = p[d] = p[d] || {};
      o._q = o._q || [];
      v = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track', 'trackAgent'];

      for (w = 0, x = v.length; w < x; ++w) {
        (function(m) {
          o[m] = o[m] || function() {
            o._q[m === v[0] ? 'unshift' : 'push']([m].concat([].slice.call(arguments, 0)));
          };
        })(v[w]);
      }

      y = e.createElement(n);
      y.async = !0;
      y.src = PENDO_CONFIG.CDN_URL + '/' + pendoKey + '/pendo.js';
      z = e.getElementsByTagName(n)[0];
      z.parentNode.insertBefore(y, z);
    })(window, document, 'script', 'pendo');

    // Initialize Pendo with options (includes visitor, account, location, and platform metadata)
    try {
      window.pendo.initialize(options);
    } catch (error) {
      console.error('[Pendo] Initialization failed:', error);
    }
  })(apiKey);
}

/**
 * Injects the Pendo agent script and initializes it with visitor and account metadata.
 * This is an internal utility - host applications should use sidekick.startPendo() instead.
 *
 * @param {string} apiKey - Pendo API key from your subscription
 * @param {Object} visitorData - Visitor identification object
 * @param {string} visitorData.id - Required: Unique visitor identifier (e.g., FLID, PersonID, or any unique user identifier)
 * @param {string} [visitorData.email] - Visitor email address
 * @param {string} [visitorData.full_name] - Visitor full name
 * @param {string} [visitorData.role] - Visitor role/permissions
 * @param {Object} accountData - Account identification object
 * @param {string} accountData.id - Required: Unique account identifier (e.g., Organization ID, Domain ID, or any unique account identifier)
 * @param {string} [accountData.name] - Account name
 * @param {number} [idleTimeout=500] - Timeout in milliseconds for requestIdleCallback fallback
 * @param {Object} [options] - Additional Pendo initialization options (e.g., location config)
 * @param {Object} [options.location] - Location configuration with transforms
 * @throws {Error} If apiKey is missing or invalid
 * @throws {Error} If visitor.id or account.id is missing
 */
export function loadPendo(apiKey, visitorData, accountData, idleTimeout, options) {
  // Validate required parameters
  if (!apiKey || typeof apiKey !== 'string') {
    console.error('Invalid API key provided. Pendo will not be loaded.');
    return;
  }

  // Prevent duplicate initialization
  if (window.pendo && window.pendo.isReady && window.pendo.isReady()) {
    console.warn('Pendo already initialized. Use pendo.identify() to update visitor or account data.');
    return;
  }

  // Initialize visitor and account with defaults if not provided
  const visitor = visitorData || { id: PENDO_CONFIG.DEFAULT_VISITOR_ID };
  const account = accountData || { id: PENDO_CONFIG.DEFAULT_ACCOUNT_ID };

  // Validate required fields after defaults applied
  if (!visitor.id) {
    console.error('Visitor ID is required (e.g., FLID, PersonID, or unique user identifier) to initialize Pendo.');
    return;
  }

  if (!account.id) {
    console.error('Account ID is required (e.g., Organization ID, Domain ID, or unique account identifier) to initialize Pendo.');
    return;
  }

  // Build complete initialization options
  const initOptions = {
    visitor: visitor,
    account: Object.assign({}, account, {
      // Platform-level tracking for Sidekick
      platformSource: 'sidekick'
    })
  };

  // Merge additional options (like location transforms)
  if (options) {
    for (const key in options) {
      if (options.hasOwnProperty(key)) {
        initOptions[key] = options[key];
      }
    }
  }

  // Default timeout to 500ms if not provided
  const timeout = typeof idleTimeout === 'number' && idleTimeout > 0 ? idleTimeout : PENDO_CONFIG.IDLE_TIMEOUT;

  // Use requestIdleCallback to load Pendo during browser idle time
  // This prevents Pendo from blocking critical rendering and user interactions
  requestIdleCallbackPolyfill(function() {
    injectPendoAgent(apiKey, initOptions);
  }, { timeout: timeout });
}

// =============================================================================
// Pendo API Wrappers
// =============================================================================

/**
 * Helper function to safely call Pendo methods with error handling.
 * @private
 */
function callPendoMethod(methodName, args, errorContext) {
  if (typeof window.pendo === 'undefined' || !window.pendo[methodName]) {
    console.warn(`Pendo not loaded. Cannot call ${methodName}.`);
    return false;
  }

  try {
    window.pendo[methodName].apply(window.pendo, args);
    return true;
  } catch (error) {
    console.error(`Failed to ${errorContext}:`, error);
    return false;
  }
}

/**
 * Wrapper for pendo.track() with safety checks.
 * Logs a custom event to Pendo analytics.
 *
 * @param {string} eventName - Name of the event to track
 * @param {Object} [properties] - Optional event properties/metadata
 */
export function track(eventName, properties) {
  callPendoMethod('track', [eventName, properties || {}], `track event: ${eventName}`);
}

/**
 * Wrapper for pendo.pageLoad() with safety checks.
 * Notifies Pendo of a page/route change (critical for SPAs).
 *
 * @param {Object} [metadata] - Optional page metadata
 * @param {string} [metadata.path] - Current path/route
 * @param {string} [metadata.name] - Page/route name
 * @param {Object} [metadata.params] - Route parameters
 */
export function pageLoad(metadata) {
  callPendoMethod('pageLoad', [metadata || {}], 'notify page load');
}

/**
 * Wrapper for pendo.initialize() with safety checks.
 * Re-initializes Pendo with new visitor and account data.
 *
 * @param {Object} options - Initialization options
 * @param {Object} options.visitor - Visitor data
 * @param {Object} options.account - Account data
 */
export function initialize(options) {
  callPendoMethod('initialize', [options], 'initialize');
}

/**
 * Wrapper for pendo.identify() with safety checks.
 * Re-identifies the visitor and account without reloading the agent.
 * Use when user context changes (e.g., org switch, role change).
 *
 * @param {Object} visitorData - Updated visitor data
 * @param {Object} accountData - Updated account data
 */
export function identify(visitorData, accountData) {
  callPendoMethod('identify', [{ visitor: visitorData, account: accountData }], 'identify user');
}

/**
 * Wrapper for pendo.updateOptions() with safety checks.
 * Updates Pendo configuration options.
 *
 * @param {Object} options - Options to update
 */
export function updateOptions(options) {
  callPendoMethod('updateOptions', [options], 'update options');
}

/**
 * Wrapper for pendo.trackAgent() with safety checks.
 * Internal method for tracking agent-level events.
 *
 * @param {Object} eventData - Event data
 */
export function trackAgent(eventData) {
  callPendoMethod('trackAgent', [eventData], 'track agent event');
}

// =============================================================================
// Pendo Location API
// =============================================================================

/**
 * Gets the Pendo location API for SPA navigation tracking.
 * Provides access to getHref(), getHash(), and setUrl() methods.
 * @returns {Object|undefined} Pendo location API or undefined if not loaded
 */
export function getLocation() {
  if (typeof window.pendo === 'undefined' || !window.pendo.location) {
    console.warn('[Pendo] Agent not loaded. Location API not available.');
    return undefined;
  }
  return window.pendo.location;
}

/**
 * Public API wrapper object for Pendo functionality.
 * Provides access to all Pendo methods with safety checks.
 */
export const pendoApi = {
  initialize,
  identify,
  updateOptions,
  pageLoad,
  track,
  trackAgent,
  get location() {
    return getLocation();
  }
};
